import { compact, partial } from 'lodash';
import React, { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import type { PrivatePortfolioNode } from 'venn-api';
import { useSendSignal } from 'venn-state';
import { SimpleMenu } from 'venn-ui-kit';
import { MANAGE_DATA_SECTION, navigateToManageDataPage, type ObjectId, useModal } from 'venn-utils';
import type { DropMenuProps } from '../../../../venn-ui-kit/src/components/drop-menu/implementations/BaseDropMenu';
import { UserContext } from '../../contexts';
import { CashflowSettingsModal } from '../../modals/cashflow-settings-modal';
import { ActionsButton, AllocatorPanelRowActionsDropMenu, GreyIcon } from '../actions/common';

interface PrivatesFundActionsProps {
  onDelete: () => void;
  subject: ObjectId;
  modifiedPortfolio: PrivatePortfolioNode;
}

export const PrivatesFundActions = ({ onDelete, subject, modifiedPortfolio }: PrivatesFundActionsProps) => {
  const { hasPermission } = useContext(UserContext);
  const hasEditHyperparametersPermission = hasPermission('EDIT_HYPERPARAMETERS');
  const [isCashflowSettingsModalOpen, openCashflowSettingsModal, closeCashflowSettingsModal] = useModal();
  const history = useHistory();

  const refreshStudioView = useSendSignal({ type: 'WorkspaceConfigurationUpdate' });
  const options = compact([
    {
      label: 'CF Pacing Model Parameters',
      value: () =>
        hasEditHyperparametersPermission
          ? openCashflowSettingsModal()
          : navigateToManageDataPage(history, subject, 'Studio', true, MANAGE_DATA_SECTION.CASH_FLOW_PACING_SETTINGS),
    },
    {
      label: 'Delete',
      value: onDelete,
    },
  ]);

  const triggerComponent: DropMenuProps<unknown>['triggerComponent'] = useCallback(
    (_, _highlighted, onToggle) => (
      <ActionsButton
        data-testid="private-allocator-fund-row-actions-button"
        onClick={partial(onToggle, undefined)}
        className="qa-strategy-actions"
      >
        <GreyIcon type="ellipsis-h" />
      </ActionsButton>
    ),
    [],
  );

  const menuComponent: DropMenuProps<unknown>['menuComponent'] = useCallback(
    (_, onCollapse, menuClassName) => (
      <SimpleMenu
        className={menuClassName}
        items={options}
        selected={null}
        onChange={(item) => {
          onCollapse();
          item?.value?.();
        }}
      />
    ),
    [options],
  );

  return (
    <>
      <AllocatorPanelRowActionsDropMenu
        style={{ position: 'absolute' }}
        className="drop-menu"
        usePortal
        filteredItems={options}
        triggerComponent={triggerComponent}
        menuComponent={menuComponent}
      />
      {isCashflowSettingsModalOpen && (
        <CashflowSettingsModal
          onChangesApplied={refreshStudioView}
          onClose={closeCashflowSettingsModal}
          privatePortfolio={modifiedPortfolio}
          triggeringFundId={subject.privateFundId}
          source="STUDIO_PRIVATE_ALLOCATOR_PANEL"
        />
      )}
    </>
  );
};
